import React from "react";
import {
  Container,
  Paper,
  Button,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ScrollToTopBtn from "../common/ScrollToTop";
import * as premiumApi from "../../api/premiumApi";
import * as dataApi from "../../api/dataApi";
import * as companyApi from "../../api/companyApi";
import PropTypes from "prop-types";
import { withRouter } from "../common/WithRouter";

const styles = {
  layout: { width: "100%", paddingTop: 80 },
  formControl: { margin: "16px", minWidth: 200, padding: "10px 0px 30px" },
  button: { marginLeft: "16px" },
};

class PremiumListAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      membership: "TFF",
      years: [],
      year: "",
      companies: [],
      companyId: "",
      premiums: [],
      premiumsDetails: [],
      pageSize: 50,
      searchQuery: "",
    };
  }

  async componentDidMount() {
    try {
      await this.getPremiums();
      await this.getYears();
      await this.getCompanies();
    } catch (error) {
      console.log("Error in componentDidMount", error);
    }
  }

  onChange = async (e) => {
    const { name, value } = e.target;

    await this.setState({ [name]: value, premiums: [] });

    if (name === "membership") {
      await Promise.all([
        this.getPremiums(),
        this.getYears(),
        this.getCompanies(),
      ]);
    } else {
      await this.getPremiums();
    }
  };

  getPremiums = async () => {
    const { membership, year, companyId } = this.state;
    const premiums = await premiumApi.getPremiums(membership, year, companyId);
    if (!premiums) return this.setState({ premiums: [] });

    if (membership === "TFF") {
      const aggregated = this.aggregateTffPremiumsByYear(premiums);
      aggregated.sort((a, b) => b.prizeYear - a.prizeYear);
      this.setState({ premiums: aggregated });
    } else {
      premiums.sort((a, b) => b.prizeYear - a.prizeYear);
      this.setState({ premiums });
    }
  };

  aggregateTffPremiumsByYear = (premiums) => {
    const grouped = {};
    premiums.forEach((premium) => {
      const key = `${premium.reportedByCompanyId}_${premium.prizeYear}`;
      if (!grouped[key]) {
        grouped[key] = {
          prizeYear: premium.prizeYear,
          reportedByCompanyId: premium.reportedByCompanyId,
          reportedByCompanyName: premium.reportedByCompanyName,
          amount: 0,
          prizeId: `${premium.reportedByCompanyId}_${premium.prizeYear}`,
          hasValidPremium: false,
        };
      }

      if (premium.amount >= 0) {
        grouped[key].amount = (grouped[key].amount || 0) + Number(premium.amount);
        grouped[key].hasValidPremium = true;
      }
      if (premium.percentShare != null) {
        grouped[key].percentShare = (grouped[key].percentShare || 0) + Number(premium.percentShare);
      }
    });

    Object.values(grouped).forEach((entry) => {
      if (!entry.hasValidPremium) {
        entry.amount = -1;
      }
    });
    return Object.values(grouped)
      .sort((a, b) => b.prizeYear - a.prizeYear);
  };


  getYears = async () => {
    const { membership } = this.state;
    const years = await dataApi.getYears(membership);
    if (years) {
      years.sort((a, b) => b - a);
      this.setState({ years });
    } else {
      this.setState({ years: [] });
    }
  };

  getCompanies = async () => {
    const { membership } = this.state;
    const companies = await companyApi.getCompaniesByOrg(membership);
    if (companies) {
      companies.sort((a, b) => a.companyName.localeCompare(b.companyName));
      this.setState({ companies });
    } else {
      this.setState({ companies: [] });
    }
  };

  render() {
    const { membership, years, year, companies, companyId, premiums } = this.state;

    const columns = [
      {
        field: "actions",
        headerName: "",
        flex: 1,
        sortable: false,
        resizable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="medium"
            color="primary"
            onClick={() =>
              this.props.navigate(`/premiumdetailadmin/${membership}/${params.row.companyId}/${params.row.year}`)
            }
          >
            <ArrowForwardIosIcon sx={{ mr: 1 }} />
            Vis Mer
          </Button>
        ),
      },
      {
        field: "prizeYear",
        headerName: "År",
        flex: 0.7,
        sortable: true,
        resizable: false,
      },
      {
        field: "reportedByCompanyName",
        headerName: "Navn",
        flex: 2,
        sortable: true,
        resizable: false,
      },
      {
        field: "amount",
        headerName: "Beløp",
        flex: 1,
        sortable: true,
        resizable: false,
        type: "number",
        valueFormatter: (params) => {
          if ((!params && params !== 0) || params < 0) {
            return "Ukjent";
          }
          return Number(params).toLocaleString("de-DE");
        },
      },
      {
        field: "percentShare",
        headerName: "Markedsandel",
        flex: 1,
        sortable: true,
        resizable: false,
        type: "number",
        valueFormatter: (params) =>
          params !== undefined && params !== null
            ? Number(params).toFixed(4).replace(".", ",")
            : "0,0000",
      },
    ];

    const formattedRows = premiums.map((premium) => ({
      prizeId: premium.prizeId,
      prizeYear: premium.prizeYear,
      reportedByCompanyName: premium.reportedByCompanyName,
      amount: premium.amount || 0,
      percentShare: premium.percentShare || 0,
      companyId: premium.reportedByCompanyId,
      year: premium.prizeYear,
    }));


    return (
      <React.Fragment>
        <Container maxWidth={false} sx={{ width: "100vw", overflowX: "hidden" }}>
          <main style={styles.layout}>
            <Paper sx={{ width: "100%", p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "normal", fontSize: "1.3rem" }}>
                PREMIEOVERSIKT
              </Typography>
              <FormControl style={styles.formControl}>
                <RadioGroup row name="membership" value={membership || ""} onChange={this.onChange}>
                  <FormControlLabel value="TFF" control={<Radio />} label="TFF" />
                  <FormControlLabel value="YFF" control={<Radio />} label="YFF" />
                </RadioGroup>
              </FormControl>

              <FormControl variant="standard" style={styles.formControl}>
                <InputLabel variant="standard">År</InputLabel>
                <Select name="year" value={year} onChange={this.onChange}>
                  <MenuItem value="">Alle</MenuItem>
                  {years.map((y, index) => (
                    <MenuItem key={index} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="standard" style={styles.formControl}>
                <InputLabel variant="standard">Bedrift</InputLabel>
                <Select name="companyId" value={companyId} onChange={this.onChange}>
                  <MenuItem value="">Alle</MenuItem>
                  {companies.map((company, index) => (
                    <MenuItem key={index} value={company.companyId}>
                      {`${company.companyName} (${company.organisationName.toUpperCase()})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* DataGrid Component */}
              <DataGrid
                style={{
                  borderStyle: "none",
                  tableLayout: "fixed",
                  width: "100%",
                  maxWidth: "100vw",
                }}
                rows={formattedRows}
                columns={columns}
                pageSizeOptions={[10, 50, 100]}
                pagination
                autoHeight
                disableSelectionOnClick
                getRowId={(row) => row.prizeId}
                sx={{
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                  },
                  "& .wrap-text": {
                    whiteSpace: "normal !important",
                    wordBreak: "break-word",
                    lineHeight: "1.2",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            </Paper>
          </main>
        </Container>
        <ScrollToTopBtn />
      </React.Fragment>
    );
  }
}

PremiumListAdmin.propTypes = {
  navigate: PropTypes.func.isRequired,
};

export default withRouter(PremiumListAdmin);
